"use client";

/**
 * The root auth-session boundary, mounted once in the root layout so `/` and
 * `/app` share one session topology. Subscribes to the token
 * provider, keys the {QueryClient, tRPC client} pair by auth-session (see
 * session-cache.ts), and publishes the auth snapshot via context. Protected
 * observers gate on `status === "authenticated"`, so nothing mounts while
 * `loading`.
 */
import { TRPC_MAX_BATCH_SIZE } from "@reflect/domain";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import type { ReactNode } from "react";
import superjson from "superjson";

import { getAnalytics } from "../analytics/index";
import { writeSessionHint } from "../auth/appGate";
import { getTokenProvider, LOADING_SNAPSHOT, type AuthSnapshot } from "../auth/index";
import { rememberSignedIn } from "../auth/signedInBefore";
import { API_URL } from "../env";
import { reconcileReflectionUploadOwner } from "../upload/reflectionUploadTask";
import { TRPCProvider, type AppRouter } from "./context";
import { createSessionCache, requestHeaders, type SessionCache } from "./session-cache";

type TrpcClient = ReturnType<typeof createTRPCClient<AppRouter>>;

/** Auth context published by the boundary. */
export interface AuthContextValue {
  snapshot: AuthSnapshot;
  /**
   * Sign out via the token provider (cache teardown follows from the snapshot
   * swap) and AWAIT analytics identity cleanup — the passive lifecycle effect
   * alone can be lost to a tab unload right after the click.
   */
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** The boundary's auth snapshot + actions. */
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthSessionBoundary>");
  return value;
}

/**
 * Who is signed in, for code that wants the answer but must not *require* the
 * boundary — `null` outside one.
 *
 * {@link useAuth} throws there, and rightly: a screen reading the session has
 * no meaning without it. The upload hook is the other case. It is mounted under
 * the boundary in the app, and is also driven bare by its own suite with
 * injected doubles; making it demand the boundary would drag the real token
 * provider and tRPC client into a unit test of the chunk protocol. Absent
 * identity is handled fail-closed downstream — see
 * `reconcileReflectionUploadOwner` — so `null` here is safe, not a silent
 * default.
 */
export function useAuthSnapshotOrNull(): AuthSnapshot | null {
  return useContext(AuthContext)?.snapshot ?? null;
}

function makeSessionCache(): SessionCache<QueryClient, TrpcClient> {
  const provider = getTokenProvider();
  return createSessionCache(() => ({
    queryClient: new QueryClient({
      defaultOptions: {
        queries: {
          // Focus refetching is opt-in per query (live reflection status comes
          // over the status stream). A 401 is an auth-state problem, not a
          // transient — never retry it.
          refetchOnWindowFocus: false,
          retry: false,
        },
      },
    }),
    trpcClient: createTRPCClient<AppRouter>({
      links: [
        httpBatchLink({
          url: API_URL,
          // The api refuses a longer batch whole; at the same cap the link
          // splits one into several requests instead.
          maxItems: TRPC_MAX_BATCH_SIZE,
          transformer: superjson,
          headers: requestHeaders(() => provider.getIdToken()),
        }),
      ],
    }),
  }));
}

/** Root client boundary: auth snapshot + session-keyed query/tRPC clients. */
export function AuthSessionBoundary({ children }: { children: ReactNode }) {
  const provider = useMemo(() => getTokenProvider(), []);
  const snapshot = useSyncExternalStore(
    provider.subscribe,
    provider.getSnapshot,
    // Server snapshot: prerender always sees "loading" (no client storage).
    () => LOADING_SNAPSHOT,
  );

  const [cache] = useState(makeSessionCache);
  // `loading` and `anonymous` are one session, holding one pair. A request
  // sent while loading waits in `getIdToken()` for the SDK to settle, and a
  // settled user publishes `authenticated` before any bearer is read, which
  // retires the pair; so the pair never keeps an identity, and a signed-out
  // visit keeps what it started while loading. Retiring it at loading →
  // anonymous cancelled the homepage's variant call on every anonymous visit,
  // and with it the landing view (#1090). Any other status keeps its own key.
  const sessionKey =
    snapshot.status === "authenticated"
      ? `uid:${snapshot.providerUid}`
      : snapshot.status === "loading" || snapshot.status === "anonymous"
        ? "signed-out"
        : snapshot.status;
  // resolve() is render-safe: idempotent per key (Strict Mode re-renders get
  // the same pair) and non-destructive — the replaced pair is only retired,
  // so a render React discards can't wipe the committed tree's cache.
  const { queryClient, trpcClient } = cache.resolve(sessionKey);

  // Commit phase: the swap is real — the keyed remount below already replaced
  // every observer — so now cancel + clear the retired pair(s); a late
  // response lands in a detached, cleared cache.
  useEffect(() => {
    cache.flushRetired();
  }, [cache, queryClient]);

  // Hand the module-scoped upload task the same news the cache teardown above
  // gets. It is the one piece of session state React cannot reach: the keyed
  // remount below replaces every observer and `flushRetired()` clears every
  // cache, but a retained recording lives outside both. See
  // `reconcileReflectionUploadOwner` for what leaks without this.
  //
  // Guarded to the two RESOLVED statuses for the same reason the session hint
  // below is: `loading` is the first frames of every visit and `unavailable`
  // means we never got an answer, and clearing on either would destroy a live
  // upload because an SDK chunk was slow.
  const ownerUid = snapshot.status === "authenticated" ? snapshot.providerUid : null;
  const ownerResolved = snapshot.status === "authenticated" || snapshot.status === "anonymous";
  useEffect(() => {
    if (ownerResolved) reconcileReflectionUploadOwner(ownerUid);
  }, [ownerResolved, ownerUid]);

  // Keep the origin's `/app/*` gate (src/proxy.ts) in step with the
  // provider. Only the two RESOLVED statuses write: `loading` is the first
  // frames of every visit, and `unavailable` means we never established an
  // answer — erasing the hint on either would bounce a signed-in visitor out
  // of their own dashboard because an SDK chunk was slow, which is precisely
  // the confident wrong answer `unavailable` exists to refuse. Written on
  // every authenticated mount rather than only at sign-in, so a hint the
  // browser expired or cleared heals on the next visit.
  //
  // The same resolved state marks the device for the homepage's Log in
  // button (`signedInBefore`). Only marked, never cleared: a signed-out
  // visitor on a device someone signed in on is still offered Log in.
  useEffect(() => {
    if (snapshot.status === "authenticated") {
      writeSessionHint(true);
      rememberSignedIn();
    } else if (snapshot.status === "anonymous") {
      writeSessionHint(false);
    }
  }, [snapshot.status]);

  // Session generation, keying the subtree below. Load-bearing: React Query
  // binds a useQuery observer to the QueryClient present at the component's
  // FIRST render (the observer lives in useState) — swapping the provider's
  // client does NOT rebind mounted observers, so without a remount a mounted
  // page keeps reading (and writing!) the previous session's cache and flashes
  // one identity's data under another. Remounting on every session switch
  // recreates all observers
  // against the new client (and drops any other component state belonging to
  // the old session).
  //
  // loading → anonymous keeps the key (see `sessionKey`), so an ordinary
  // signed-out page load reuses its hydration output and its observers stay
  // on the live pair. loading → authenticated MUST bump: with a persisted
  // session, the observers mounted during hydration would otherwise stay bound
  // to the retired signed-out pair — flushRetired() then cancels/clears their
  // first `me.get` and its result would live outside the session cache.
  const [generation, setGeneration] = useState({ key: sessionKey, n: 0 });
  if (generation.key !== sessionKey) {
    // Render-phase derived-state adjustment (the React-sanctioned pattern).
    setGeneration({ key: sessionKey, n: generation.n + 1 });
  }

  const auth = useMemo<AuthContextValue>(
    () => ({
      snapshot,
      signOut: async () => {
        await provider.signOut();
        // Idempotent with the lifecycle effect's reconcile — whichever runs
        // second sees no previous identity and no-ops.
        // A client that failed to load must not fail a sign-out that succeeded.
        await getAnalytics()
          .then((analytics) => analytics.reconcileIdentity(null))
          .catch(() => undefined);
      },
    }),
    [snapshot, provider],
  );

  return (
    <AuthContext.Provider value={auth}>
      <QueryClientProvider key={generation.n} client={queryClient}>
        <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
          {children}
        </TRPCProvider>
      </QueryClientProvider>
    </AuthContext.Provider>
  );
}
